<template>
    <article aria-label="Question" :id="'question-' + activeQuestion.TEST_QUESTION_RID" class="question-display">
        <div class="sr-only">
            <p>
							{{ $t('MAIN_CONTENT.testTaker.questionNumber', { number: activeQuestion.index + 1 } ) }}
						</p>
        </div>
        <div class="pull-right">
            <span class="pill pill-dark">
							{{ $t('MAIN_CONTENT.MatchingQuestion.pointsPossible', { points: activeQuestion.TOTAL_POINTS } ) }}
						</span>
        </div>
        <div class="question-type-instructions">
            <strong>{{ activeQuestion.QUESTION_NAME }}</strong>
            <span class="hint">
							{{ $t('MAIN_CONTENT.MatchingQuestion.instruction') }}
						</span>
        </div>

        <div v-if="isGraded( activeQuestion )">
            <div class="answers">
                <div
                    v-for="subQ in subQuestionMatches"
                    :key="subQ.TEST_QUESTION_RID" 
                    :class="getAnswerClass( subQ.ANS_CORRECT_FLG )">
                    <div class="answer-number selected">
                        <i class="fa fas fa-arrow-circle-right" />
                        <div
                            v-if="subQ.ANS_CORRECT_FLG"
                           aria-label="Points Earned"
                           class="badge badge-dark answer-points float-right"
                           >
														{{ $t('MAIN_CONTENT.testTaker.points', { points: subQ.POINTS_EARNED.toFixed(2) } ) }}
                        </div>
                    </div>
                    <div
                        class="question-text"
                        v-html="getAnswerText(subQ.ANSWER_RID)" />
                    <div
                        class="answer-text"
                        v-html="subQ.QUESTION_TEXT" />
                     <div class="question-text" v-if="!subQ.ANS_CORRECT_FLG && testMode == 'grading'">
                        {{ $t('MAIN_CONTENT.MatchingQuestion.correctAnswer') }}
                        <span v-html="getCorrectAnswerText(subQ.QUESTION_TEXT)"></span> 
                    </div>
                    <div class="grade-result">
                        <i :class="getFeedbackIcon( subQ.ANS_CORRECT_FLG )" />
                    </div>
                </div>
            </div>
            
            <question-summary />
        </div>

        <!-- show the form inputs -->
        <div v-else>
            <div class="question-type-instructions">
                <span>
									{{ $t('MAIN_CONTENT.MatchingQuestion.instruction') }}
								</span>
            </div>

            <h3
                class="question-text"
                v-html="activeQuestion.QUESTION_TEXT" />

            <div class="matching-pairs answer row">
                <div class="col-sm-6 answers">
                    <div
                        v-for="( prompt, i ) in subQuestions"
                        :key="prompt.TEST_QUESTION_RID"
                        class="matching-prompt form-inline clearfix">
                        <div class="col-sm-2">
                            <select
                                :ref="`answerRef-${i}`"
                                class="form-control"
                                @change="answerTheQuestion( $event, i )">
                                <option v-if="workingAnswers[i].ANSWER_RID === ''" selected="true"></option>
                                <option
                                    v-for="( itm, j ) in promptAnswers"
                                    :aria-label="itm.TEST_QUESTION_ANSWER_TEXT"
                                    :key="j"
                                    :value="j"
                                    :selected="itm.ANSWER_RID === workingAnswers[i].ANSWER_RID ? true : false"
                                    >
                                    {{ getAnswerChoice( j ) }}
                                </option>
                            </select>
                        </div>
                        <div v-html="prompt.QUESTION_TEXT" class="col-sm-10"></div>
                    </div>
                </div>

                <div class="answers col-xs-6">
                    <div
                        v-for="( answer, index ) in promptAnswers" 
                        :key="index" 
                        class="clickable answer">
                        <div class="answer-number">
                            {{ getAnswerChoice( index ) }}
                        </div>
                        <div
                            class="answer-text"
                            v-html="answer.ANSWER_TEXT" />
                    </div>
                </div>
            </div>
        </div>
        <test-buttons
            v-if="testMode !== 'grading'"
            @answer-save="submitAnswer" />
    </article>
</template>

<script>
import { mapState,mapActions,mapMutations } from "vuex";
import testButtons from "./testButtons.vue";
import questionSummary from "./questionSummary.vue";

export default {
    components : { testButtons, questionSummary },
    props      : {
        testPreview: {
            type     : Object,
            required : true
        },
        activeQuestion: {
            type     : Object,
            required : true
        },
        studentAnswer: {
            type    : Array,
            default : function(){
                return {};
            }
        },
        studentAnswers: {
            type    : Object,
            default : function(){
                return {};
            }
        },
        questions: {
            type    : Array,
            default : function(){
                return [];
            }
        },
        answers: {
            type    : Array,
            default : function(){
                return [];
            }
        },
        test: {
            type    : Object,
            default : function(){
                return {};
            }
        },
        testMode: {
            type    : String,
            default : "student"
        }
    },
    data(){
        return{
            // since the subquestions are each questions themselves, we'll use an array of answerData objects
            workingAnswers : [],
            // local object to manage selected answer so that it will remain populated if jumping back to question
            answerData     : {},
            requestQueue: [], // Queue to manage requests
            isProcessing: false, // Flag to check if a request is being processed
        };
    },
    computed: {
        allowChanges (){
            return this.test.ALLOW_ANSWER_CHANGE;
        },
        // the answers for the group of subquestions currently displayed
        promptAnswers (){
            return this.answers.filter( ans =>{
                return ans.TEST_QUESTION_PARENT_RID === this.activeQuestion.TEST_QUESTION_RID; 
            } );
        },
        // the subquestions for the currently displayed question
        subQuestions (){
            return this.questions.filter( q => {
                return q.TEST_QUESTION_PARENT_RID === this.activeQuestion.TEST_QUESTION_RID && q.QUESTION_TEXT !== ''; 
            } ) ;
        },
        // track the order of the subquestions ( since they may be shuffled ) so we can compare the selected answers to the right subquestion
        subQuestionSequence (){
            return this.subQuestions.map( q=>{
                return q.TEST_QUESTION_RID; 
            } );
        },
        // creates a mix of the subquestion and answer fields needed for read-only display
        subQuestionMatches (){
            let matches = [];
            for( let i = 0; i < this.subQuestions.length; i++ ){
                let q = this.subQuestions[ i ];
                let a = {};
                if(this.testMode === "grading" && !this.testPreview.TEST.GRADING_COMPLETE_FLG){
                     a = this.studentAnswer[0].SUB_QUESTIONS[q.TEST_QUESTION_RID];
                     a.POINTS_EARNED = Number(a.POINTS_EARNED);
                     let pickedAnswer = this.studentAnswer[0].SUB_QUESTIONS[q.TEST_QUESTION_RID].ACTUAL_ANSWER;
                     if(pickedAnswer){
                         let answer = this.answers.find((ans)=>{
                             return Number(ans.TEST_QUESTION_RID) === Number(pickedAnswer);
                         });
                         if(answer){
                             a.TEST_QUESTION_ANSWER_TEXT = answer.TEST_QUESTION_ANSWER_TEXT;
                             a.ANSWER_RID = answer.ANSWER_RID;
                         }
                     }
                } else {
                    a = this.studentAnswer[ i ];
                }
                
                if(typeof a === "undefined"){
                    a = {};
                }
                let match = {
                    QUESTION_TEXT             : q.QUESTION_TEXT,
                    TEST_QUESTION_ANSWER_TEXT : !!a.TEST_QUESTION_ANSWER_TEXT ? a.TEST_QUESTION_ANSWER_TEXT : "",
                    ANS_CORRECT_FLG           : !!a.ANSWER_CORRECT_FLG ? a.ANSWER_CORRECT_FLG : "",
                    POINTS_EARNED             : !!a.POINTS_EARNED ? a.POINTS_EARNED : "",
                    ANSWER_RID                : a.ANSWER_RID || ""
                };
                matches.push( match );
            }
            return matches;
        },
        pointsEarned (){
            let total = 0;
            this.studentAnswer.forEach( ans =>{
                total += ans.POINTS_EARNED;
            } );
            return total;
        }
    },
    watch: {
        activeQuestion: {
            immediate: true,
            handler: function(newValue, oldValue) {
                this.answerData.questionId = this.activeQuestion.TEST_QUESTION_RID;
                this.answerData.testId = this.activeQuestion.TEST_RID;
                this.populateForm();
                this.$nextTick(()=>{
                    try{
                        document.getElementById( "testZone" ).focus();
                    }catch(e){}
                })
            },
        },
    },
    methods: {
        ...mapMutations( [ "updateState","setUnsaved" ] ),
        ...mapActions( [ "update","scoreAnswer","postAnswer" ] ),
            getAnswerStruct(itm, questionRid){
                return {
                    "ANSWER_RID": itm.ANSWER_RID,
                    "ERROR_MARGIN": itm.ERROR_MARGIN,
                    "ANSWER_CORRECT_FLG": itm.ANSWER_CORRECT_FLG,
                    "ANSWER_TEXT": itm.ANSWER_TEXT,
                    "POINTS": itm.POINTS,
                    "GENERAL_ANSWER_FEEDBACK": itm.GENERAL_ANSWER_FEEDBACK,
                    "TEST_RID": itm.TEST_RID,
                    "TEST_QUESTION_PARENT_RID": itm.TEST_QUESTION_PARENT_RID,
                    "TEST_QUESTION_RID": questionRid
                };
            },
            getAnswerText(answerRid){
                let answer = this.promptAnswers.filter(item=>{
                    return item.ANSWER_RID === answerRid;
                });
                if(answer && answer.length === 1){
                    return answer[0].ANSWER_TEXT;
                }
                return answerRid;
            },
            getCorrectAnswerText(questionText){
                let answerText = "error";
                let question = this.subQuestions.find(item=>{
                    return item.QUESTION_TEXT === questionText;
                });
                console.log(question)
                if(question){
                    let answer = this.promptAnswers.find(item=>{
                        return item.TEST_QUESTION_RID === question.TEST_QUESTION_RID;
                    });
                    console.log(answer)
                    if(answer) answerText = answer.ANSWER_TEXT;
                }

                return answerText;
            },

        // returns the student answer for a particular subquestion
        getStudentAnswerForMatch ( q ){
            return this.studentAnswer.filter( ans =>{
                return ans.TEST_QUESTION_RID === q.TEST_QUESTION_RID; 
            } )[ 0 ];
        },
        // prepare data for save
        answerTheQuestion ( event, index ){
						this.workingAnswers[index] = this.getAnswerStruct(this.promptAnswers[event.target.value], this.subQuestions[index].TEST_QUESTION_RID);
            // question has been answered, so no changes
            if( this.isGraded( this.workingAnswers[ index ] ) && !this.allowChanges ) return;
						// tracking unsaved responses
						this.workingAnswers[ index ].TEST_QUESTION_RID = this.subQuestionSequence[index];
						this.answerData = {
                testId     : this.activeQuestion.TEST_RID,
								questionId : this.workingAnswers[ index ].TEST_QUESTION_PARENT_RID,
								questionType: this.activeQuestion.QUESTION_TYPE,
								answer     : this.workingAnswers,
								subQSequence: this.subQuestionSequence,
						};

            this.setUnsaved( { key: this.workingAnswers[ index ].TEST_QUESTION_PARENT_RID, value: JSON.parse( JSON.stringify(this.answerData)) } );
            // save the answer
            
            let payload = {};
            payload = this.answerData;
            const requestObj = {
                key: this.answerData.questionId,
                data: JSON.stringify(payload),
                queryParams: {},
                routeParams: {
                    action: "answer-question",
                    testRid: this.$route.params.testRid,
                },
                index: index, // Keep track of which question this is for
            };

            this.requestQueue.push(requestObj); // Add request to queue

            if (!this.isProcessing) {
                this.processQueue();
            }
        },
        async processQueue() {
            this.isProcessing = true;

            while (this.requestQueue.length > 0) {
                const currentRequest = this.requestQueue.shift(); // Get the first request from the queue

                let attempts = 0;
                const maxRetries = 5;
                let success = false;

                while (attempts < maxRetries && !success) {
                    try {
                        await this.postAnswer(currentRequest); // Attempt the request
                        this.setUnsaved({ key: this.workingAnswers[currentRequest.index].TEST_QUESTION_PARENT_RID, value: "" });
                        success = true; // Mark the request as successful
                    } catch (error) {
                        attempts++;
                        console.error(`Attempt ${attempts} failed:`, error);

                        if (attempts >= maxRetries) {
                            alert('An error occured while saving....');
                            // Optionally, handle the failure here, e.g., log it, notify the user, etc.
                        } else {
                            console.log(`Retrying... (${attempts}/${maxRetries})`);
                        }
                    }
                }
            }

            this.isProcessing = false; // No more requests to process
        },
        getFeedbackIcon ( flg ){
            return flg ? "fa fas fa-check-circle" :"fa fas fa-times-circle";
        },
        // returns either letter, number or roman numeral for the answer choice
        getAnswerChoice ( index ){
            return this.numToLetters(index+1);
        },
        numToLetters(v) {
            let result = [];
            let accum = Number(v);
            if (accum < 1) {
                return "";
            }
            while (accum>0) {
                let rem = (accum-1)%26;
                result.splice(0,0,String.fromCharCode(65+rem));
                accum = (accum - rem - 1)/26;
            }
            return result.join("");
        },
        getAnswerClass ( flag ){
            let cls = "answer";
            if( !this.isGraded( this.activeQuestion ) ) cls += " clickable";
            let cls2 = flag ? " correct" : " incorrect";
            return cls + cls2;
        },
        answeredByStudent (){
            return Object.prototype.hasOwnProperty.call( this.studentAnswers, this.activeQuestion.TEST_QUESTION_RID ); 
        },

        // the Fischer-Yates ( aka Knuth ) Shuffle
        shuffle ( array ){
            let currentIndex = array.length, temporaryValue, randomIndex;
            // While there remains elements to shuffle
            while( 0 !== currentIndex ){
                // Pick a remaining element
                randomIndex = Math.floor( Math.random() * currentIndex );
                currentIndex -= 1;
                // Swap it with the current element
                temporaryValue = array[ currentIndex ];
                array[ currentIndex ] = array[ randomIndex ];
                array[ randomIndex ] = temporaryValue;
            }
            return array;
        },
        // returns true if question has been graded
        isGraded ( answer ){
            if(this.testMode === "grading") return true;
            if( !answer ) return false;
            return Object.prototype.hasOwnProperty.call( this.studentAnswers, answer.TEST_QUESTION_RID ) 
                    && this.studentAnswers[ answer.TEST_QUESTION_RID ].filter( ans =>{
                        return ans.QUESTION_GRADED_STATUS; 
                    } ).length > 0;
        },
        populateForm (){
					this.workingAnswers = [];
					/* for each question match to the student answer */

					// Build default empty working answers array
					this.subQuestionSequence.forEach( questionRid => {
						this.workingAnswers.push({
							"ANSWER_RID": "",
							"ERROR_MARGIN": "",
							"ANSWER_CORRECT_FLG": "",
							"ANSWER_TEXT": "",
							"POINTS": "",
							"GENERAL_ANSWER_FEEDBACK": "",
							"TEST_RID": "",
							"TEST_QUESTION_PARENT_RID": "",
							"TEST_QUESTION_RID": questionRid
						});
					});

					for( let i=0; i < this.workingAnswers.length; i++ ){
						for( let j=0; j < this.studentAnswer.length; j++ ){
							if( this.workingAnswers[i].TEST_QUESTION_RID === this.studentAnswer[j].TEST_QUESTION_RID ){
								this.workingAnswers[i] = this.getAnswerStruct(this.studentAnswer[j], this.workingAnswers[i].TEST_QUESTION_RID );
							}
						}
					}
        },
        focusInput ( inputRef ) {
            this.$refs[inputRef].focus();
        },
        submitAnswer (){
            if( !this.answerData.questionId ) return;
            // clear this answer from unsaved
            this.setUnsaved( { key: this.answerData.questionId, value: "" } );
            // save the answer
            this.answerData = {
                testId     : this.activeQuestion.TEST_RID,
                questionId : this.activeQuestion.TEST_QUESTION_RID,
                answer     : this.workingAnswers
            };
            
            let payload = {};
            payload = this.answerData;
            payload.subQSequence = this.subQuestionSequence;
            this.scoreAnswer( { 
                    "key": this.answerData.questionId,
                    "data": JSON.stringify(payload),
                    "queryParams":{},
                    "routeParams":{ 
                        action: "score-question", 
                        testRid: this.$route.params.testRid
                    } 
                }
            );
        }
    },
};
</script>
